import api from '@/api/axiosApi'
import type {
  DagMetadataResponse,
  DagTableLoadResponse,
  DagTableSearchRequest,
  BatchPatchRequest,
  BatchPatchResponse,
  BulkColumnEditRequest,
  BulkColumnEditResponse,
  DropdownAllResponse,
  ReorderLineItemRequest,
} from '@/types/dag'

export async function fetchDagMetadata(
  planId: string,
  versionId: string
): Promise<DagMetadataResponse> {
  const { data } = await api.get<DagMetadataResponse>(
    `/plans/${planId}/versions/${versionId}/table/dag-metadata`
  )
  return data
}

export async function fetchDagRows(
  planId: string,
  versionId: string,
  offset: number,
  limit: number,
  searchQuery?: string
): Promise<DagTableLoadResponse> {
  const { data } = await api.get<DagTableLoadResponse>(
    `/plans/${planId}/versions/${versionId}/table/rows`,
    {
      params: {
        offset,
        limit,
        include_aggregates: true,
        sort: '-created_at',
        ...(searchQuery ? { q: searchQuery } : {}),
      },
    }
  )
  return data
}

const LOCATE_ROW_PAGE_SIZE = 1000

/**
 * Finds the position of a row (by its stable line_ref_id) within the
 * unfiltered table order. Returns null if the row isn't in the version.
 */
export async function findDagRowIndexByLineRef(
  planId: string,
  versionId: string,
  lineRefId: string
): Promise<number | null> {
  let offset = 0
  for (;;) {
    const { data } = await api.get<DagTableLoadResponse>(
      `/plans/${planId}/versions/${versionId}/table/rows`,
      {
        params: {
          offset,
          limit: LOCATE_ROW_PAGE_SIZE,
          include_aggregates: false,
          columns: 'sku_number',
        },
      }
    )
    const idx = data.rows.findIndex(r => r.line_ref_id === lineRefId)
    if (idx !== -1) return offset + idx
    if (!data.pagination.has_more || data.rows.length === 0) return null
    offset += LOCATE_ROW_PAGE_SIZE
  }
}

export async function searchDagRows(
  planId: string,
  versionId: string,
  payload: DagTableSearchRequest
): Promise<DagTableLoadResponse> {
  const { data } = await api.post<DagTableLoadResponse>(
    `/plans/${planId}/versions/${versionId}/table/rows/search`,
    payload
  )
  return data
}

export async function patchDagRows(
  planId: string,
  versionId: string,
  payload: BatchPatchRequest
): Promise<BatchPatchResponse> {
  const { data } = await api.patch<BatchPatchResponse>(
    `/plans/${planId}/versions/${versionId}/table/rows`,
    payload
  )
  return data
}

export async function bulkEditColumn(
  planId: string,
  versionId: string,
  payload: BulkColumnEditRequest
): Promise<BulkColumnEditResponse> {
  const { data } = await api.post<BulkColumnEditResponse>(
    `/plans/${planId}/versions/${versionId}/table/rows/bulk-column-edit`,
    payload
  )
  return data
}

export async function reorderDagRow(
  planId: string,
  versionId: string,
  lineId: string,
  rowVersion: number,
  anchor: ReorderLineItemRequest
): Promise<{ id: string; row_version: number }> {
  const { data } = await api.post<{ id: string; row_version: number }>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/reorder`,
    anchor,
    { headers: { 'If-Match': String(rowVersion) } }
  )
  return data
}

export async function fetchDropdowns(): Promise<DropdownAllResponse> {
  const { data } = await api.get<DropdownAllResponse>(
    '/api/v1/lookups/dropdowns'
  )
  return data
}
